import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import { useFlaggingCallback } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { isModifierKeyPressed } from '@databases/react';
import { SkeletonItem } from '@databases/skeleton';
import {
  DatabaseFieldType,
  DatabaseValue,
  SelectOption,
} from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import React, { forwardRef } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const StyledButton = styled(Button)`
  && {
    height: 40px;
    font-size: 12px;
    align-items: center;

    > * {
      flex-grow: 0;
    }
  }

  svg {
    height: 18px;
    width: auto;
  }
`;

interface BoardLayoutAddEntryButtonProps {
  viewSettings: DatabaseViewSettings;
  option: SelectOption | undefined;
}

const BoardLayoutAddEntryButton = forwardRef<HTMLElement, BoardLayoutAddEntryButtonProps>(
  function BoardLayoutAddEntryButton({ viewSettings, option }, ref) {
    const { showPanel } = usePanel();

    const { formatMessage } = useIntl();

    const handleClick = useFlaggingCallback(
      async (e: React.MouseEvent | React.KeyboardEvent) => {
        const customValues: Record<string, DatabaseValue> = {
          [viewSettings.layout.groupFieldId!]: {
            type: DatabaseFieldType.SELECT,
            value: option ? [option.id ?? option.label] : [],
          },
        };
        if (!isModifierKeyPressed(e)) {
          e.preventDefault();
          showPanel('CreateEntryPanel', {
            customValues,
          });
        }
      },
      [viewSettings.layout.groupFieldId, option, showPanel]
    );

    return (
      <SkeletonItem isButton hasIcon height={20} maxWidth={80}>
        <StyledButton
          onClick={handleClick}
          iconBefore={<AddIcon label="" />}
          appearance="subtle"
          ref={ref}
        >
          {formatMessage(messages['layout.board-add-entry'])}
        </StyledButton>
      </SkeletonItem>
    );
  }
);

export default BoardLayoutAddEntryButton;
